import Music from "@/models/Music";
import { PLAY_STYLE, PLAY_STYLE_STRING, PlayStyle } from "@/common/types/PlayStyle";
import { Difficulty, DIFFICULTY, DIFFICULTY_STRING } from "@/common/types/Difficulty";
import { DJ_LEVEL, DjLevel } from "@/common/types/DjLevel";
import { ChartData } from "@/common/types/MasterData";
import { Level } from "@/common/types/Level";
import { Radar, RADAR_ELEMENT_KEY, RADAR_ORDER, RadarElement, valueFormat } from "@/common/types/Radar";

export default class Chart {
  readonly music: Music;
  readonly playStyle: PlayStyle;
  readonly difficulty: Difficulty;
  private readonly _minBpm: number | null;
  private readonly _maxBpm: number | null;
  readonly level: Level | null;
  readonly notes: number | null;
  readonly radar: Radar | null;

  constructor(music: Music, data: ChartData) {
    this.music = music;
    this.playStyle = data.playStyle;
    this.difficulty = data.difficulty;
    this._minBpm = data.minBpm;
    this._maxBpm = data.maxBpm;
    this.level = data.level;
    this.notes = data.notes;

    this.radar = data.radar;

    music.chart(this);
  }

  get playStyleString(): string {
    return PLAY_STYLE_STRING[this.playStyle];
  }

  get playStyleChar(): string {
    return PLAY_STYLE_STRING[this.playStyle].charAt(0);
  }

  get difficultyString(): string {
    return DIFFICULTY_STRING[this.difficulty];
  }

  get difficultyChar(): string {
    return DIFFICULTY_STRING[this.difficulty].charAt(0);
  }

  get chartType(): string {
    return this.playStyleString + this.difficultyChar;
  }

  get minBpm(): number {
    return this._minBpm === null ? this.music.minBpm : this._minBpm;
  }

  get maxBpm(): number {
    return this._maxBpm === null ? this.music.maxBpm : this._maxBpm;
  }

  get isSoflan(): boolean {
    return this.minBpm !== this.maxBpm;
  }

  get bpmString(): string {
    return this.isSoflan ? `${this.minBpm}-${this.maxBpm}` : this.minBpm.toString();
  }

  get levelString(): string {
    return this.level === null ? "-" : this.level.toString();
  }

  get notesString(): string {
    return this.notes === null ? "-" : this.notes.toString();
  }

  djLevelScore(djLevel: DjLevel): number | null {
    if (this.notes === null) return null;

    if (djLevel < DJ_LEVEL.E) {
      return 0;
    }
    return Math.ceil(this.notes * 2 * (djLevel / DJ_LEVEL.MAX));
  }

  textage(option?: { playSide?: 1 | 2; highSpeed?: 0 | 1 | 2 | 3; slim?: boolean }): string | null {
    if (this.music.textage === null) return null;

    let p = this.playStyle === PLAY_STYLE.SP ? "1" : "D";
    const d = this.difficulty === DIFFICULTY.LEGGENDARIA ? "X" : this.difficultyChar;
    const l = this.level === null ? 0 : this.level.toString(16).toUpperCase();
    let h = [6, 0, 2, 4][option?.highSpeed ?? 1];

    if (option !== undefined) {
      p = this.playStyle === PLAY_STYLE.SP && option.playSide === 2 ? "2" : p;
      h = option.slim ? h + 1 : h;
    }

    return this.music.textage + `?${p}${d}${l}${h}0`;
  }

  get isMostDifficult(): boolean {
    return this.music.mostDifficultChart(this.playStyle) === this;
  }

  get maxRadarElements(): RadarElement[] | null {
    if (this.radar === null) return null;

    const maxValue = Math.max(...Object.values(this.radar));

    return RADAR_ORDER.map((e) => ({
      key: e,
      value: (this.radar ?? {})[RADAR_ELEMENT_KEY[e]],
    }))
      .filter((e) => e.value === maxValue)
      .map((e) => e.key);
  }

  get radarString(): { [P in keyof Radar]: string } {
    return {
      notes: this.radar ? valueFormat(this.radar.notes) : "-",
      chord: this.radar ? valueFormat(this.radar.chord) : "-",
      peak: this.radar ? valueFormat(this.radar.peak) : "-",
      charge: this.radar ? valueFormat(this.radar.charge) : "-",
      scratch: this.radar ? valueFormat(this.radar.scratch) : "-",
      soflan: this.radar ? valueFormat(this.radar.soflan) : "-",
    };
  }
}
